import { useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCw, Loader2 } from "lucide-react";
import { countPending, listPending, onQueueChange, syncAll } from "@/lib/offlineQueue";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

/**
 * ConnectionBadge — Indicador global de conectividad + cola de pedidos offline.
 *
 * - Verde 'Online' → todo sincronizado, hay red.
 * - Ámbar 'Sin conexión · N pendientes' → offline con pedidos en cola local.
 * - Rojo 'Sin sync · N pendientes' → hay red pero fallaron subidas (backend caído, 401, etc.).
 *
 * Click abre popover con la lista de pedidos pendientes y botón "Sincronizar ahora".
 */
export default function ConnectionBadge() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [items, setItems] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncOk, setLastSyncOk] = useState(true);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const refresh = async () => {
    const [n, list] = await Promise.all([countPending(), listPending()]);
    setPending(n);
    setItems(list);
  };

  useEffect(() => {
    refresh();
    const unsub = onQueueChange(refresh);
    const t = setInterval(refresh, 15000);
    return () => { unsub(); clearInterval(t); };
  }, []);

  const doSync = async () => {
    if (!online) { toast.info("Aún sin conexión. Volveremos a intentarlo cuando vuelva la red."); return; }
    if (pending === 0) { toast.info("Todo está sincronizado."); return; }
    setSyncing(true);
    try {
      const res = await syncAll(api);
      const okCount = (res?.results || []).filter((r) => r.ok).length;
      const failCount = (res?.results || []).filter((r) => !r.ok).length;
      setLastSyncOk(failCount === 0);
      await refresh();
      if (failCount === 0) toast.success(`${okCount} pedido${okCount === 1 ? "" : "s"} sincronizado${okCount === 1 ? "" : "s"} ✅`);
      else toast.warning(`${okCount} subidos · ${failCount} fallaron. Reintentaremos.`);
    } catch (e) {
      setLastSyncOk(false);
      toast.error("Falló la sincronización. Reintentaremos.");
    } finally {
      setSyncing(false);
    }
  };

  const state = !online ? "offline" : (pending > 0 ? (lastSyncOk ? "pending" : "error") : "ok");

  const styles = {
    ok:      { bg: "bg-emerald-50 border-emerald-200 text-emerald-800", icon: <Wifi className="w-3.5 h-3.5" />, label: "En línea" },
    offline: { bg: "bg-amber-50 border-amber-300 text-amber-900",       icon: <WifiOff className="w-3.5 h-3.5" />, label: `Sin conexión${pending > 0 ? ` · ${pending} en cola` : ""}` },
    pending: { bg: "bg-blue-50 border-blue-300 text-blue-900",          icon: <RefreshCw className="w-3.5 h-3.5" />, label: `${pending} por sincronizar` },
    error:   { bg: "bg-rose-50 border-rose-300 text-rose-900",          icon: <WifiOff className="w-3.5 h-3.5" />, label: `${pending} sin sync · reintentar` },
  }[state];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${styles.bg} hover:opacity-90 transition`}
          data-testid="connection-badge"
          title="Estado de conexión y pedidos pendientes"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : styles.icon}
          <span className="hidden sm:inline">{styles.label}</span>
          {state !== "ok" && <span className="sm:hidden">{pending || (online ? "" : "off")}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Cola de sincronización</div>
          <button
            onClick={doSync}
            disabled={syncing || pending === 0 || !online}
            className="text-xs text-primary hover:underline disabled:opacity-50 disabled:no-underline flex items-center gap-1"
            data-testid="sync-now-btn"
          >
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Sincronizar
          </button>
        </div>
        {pending === 0 ? (
          <div className="text-xs text-muted-foreground py-4 text-center">
            {online ? "Todo sincronizado." : "Estás sin conexión. Los pedidos que crees quedarán en cola aquí."}
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-1">
            {items.map((it) => (
              <div key={it.localId} className="text-xs p-2 border rounded-md bg-muted/30">
                <div className="font-medium">{it.snapshot?.cliente_nombre || "Cliente"}</div>
                <div className="text-muted-foreground flex justify-between mt-0.5">
                  <span>{(it.snapshot?.items || []).length} ítem(s)</span>
                  <span className="tabular-nums">${(it.snapshot?.total || 0).toLocaleString("es-CO")}</span>
                </div>
                <div className="text-[10px] text-muted-foreground/80 mt-0.5">
                  Creado {it.createdAt ? new Date(it.createdAt).toLocaleString() : "-"}
                  {it.retries > 0 && <span className="text-rose-600 ml-1">· {it.retries} reintento{it.retries === 1 ? "" : "s"}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
